interface RuleLink {
  text: string;
  link: string;
}

interface RuleText {
  description: string;
  details: string[];
  sources: string[];
  sourceLinkTop: RuleLink;
  sourceLinkBot?: RuleLink;
  sourcesMore?: string[];
}

export const RULES_INFO_COMPLETENESS_TRANSLATION_FILE =
  'rules-info-completeness';

export const RULES_TECHNICAL_ACCESSIBILITY_TRANSLATION_FILE =
  'rules-technical-accessibility';

const TECHNICAL_ACCESSIBILITY_RULE_TEXT_KEYS = new Set([
  'robots-txt',
  'file',
  'structure',
  'products_section',
  'schema_exists',
  'organization_schema',
  'website_schema',
  'js_crawlability',
  'speed_of_load',
  'core_web_vitals',
]);

export const getRuleTextTranslationFile = (ruleKey: string) => {
  return TECHNICAL_ACCESSIBILITY_RULE_TEXT_KEYS.has(ruleKey)
    ? RULES_TECHNICAL_ACCESSIBILITY_TRANSLATION_FILE
    : RULES_INFO_COMPLETENESS_TRANSLATION_FILE;
};

export const INFO_COMPLETNESS_RULES_TEXT: Record<string, RuleText> = {
  presentation_quality: {
    description: 'presentationQualityDescription',
    details: [
      'presentationQualityDetail1',
      'presentationQualityDetail2',
      'presentationQualityDetail3',
      'presentationQualityDetail4',
    ],
    sourceLinkTop: {
      text: 'presentationQualitySourceTopTitle',
      link: 'https://arxiv.org/abs/2409.11491',
    },
    sources: [
      'presentationQualitySourceTopBullet1',
      'presentationQualitySourceTopBullet2',
      'presentationQualitySourceTopBullet3',
      'presentationQualitySourceTopBullet4',
    ],
    sourcesMore: [
      'presentationQualitySourceBotBullet1',
      'presentationQualitySourceBotBullet2',
      'presentationQualitySourceBotBullet3',
      'presentationQualitySourceBotBullet4',
    ],
    sourceLinkBot: {
      text: 'presentationQualitySourceBotTitle',
      link: 'https://aclanthology.org/E17-3014.pdf',
    },
  },
  consistency_across_pages: {
    description: 'consistencyAcrossPagesDescription',
    details: [
      'consistencyAcrossPagesDetail1',
      'consistencyAcrossPagesDetail2',
      'consistencyAcrossPagesDetail3',
      'consistencyAcrossPagesDetail4',
    ],
    sources: [
      'consistencyAcrossPagesSourceTopBullet1',
      'consistencyAcrossPagesSourceTopBullet2',
      'consistencyAcrossPagesSourceTopBullet3',
      'consistencyAcrossPagesSourceTopBullet4',
    ],
    sourcesMore: [
      'consistencyAcrossPagesSourceBotBullet1',
      'consistencyAcrossPagesSourceBotBullet2',
      'consistencyAcrossPagesSourceBotBullet3',
      'consistencyAcrossPagesSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'consistencyAcrossPagesSourceTopTitle',
      link: 'https://arxiv.org/abs/2504.00180',
    },
    sourceLinkBot: {
      text: 'consistencyAcrossPagesSourceBotTitle',
      link: 'https://arxiv.org/abs/2504.05767',
    },
  },
  specificity: {
    description: 'specificityDescription',
    details: [
      'specificityDetail1',
      'specificityDetail2',
      'specificityDetail3',
      'specificityDetail4',
    ],
    sources: [
      'specificitySourceTopBullet1',
      'specificitySourceTopBullet2',
      'specificitySourceTopBullet3',
      'specificitySourceTopBullet4',
    ],
    sourcesMore: [
      'specificitySourceBotBullet1',
      'specificitySourceBotBullet2',
      'specificitySourceBotBullet3',
      'specificitySourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'specificitySourceTopTitle',
      link: 'https://aclanthology.org/2024.eacl-long.172.pdf',
    },
    sourceLinkBot: {
      text: 'specificitySourceBotTitle',
      link: 'https://arxiv.org/abs/2404.11972',
    },
  },
  llm_citation_potential: {
    description: 'LLMCitationPotentialDescription',
    details: [
      'llmCitationPotentialDetail1',
      'llmCitationPotentialDetail2',
      'llmCitationPotentialDetail3',
      'llmCitationPotentialDetail4',
    ],
    sources: [
      'llmCitationPotentialSourceTopBullet1',
      'llmCitationPotentialSourceTopBullet2',
      'llmCitationPotentialSourceTopBullet3',
      'llmCitationPotentialSourceTopBullet4',
    ],
    sourcesMore: [
      'llmCitationPotentialSourceBotBullet1',
      'llmCitationPotentialSourceBotBullet2',
      'llmCitationPotentialSourceBotBullet3',
      'llmCitationPotentialSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'llmCitationPotentialSourceTopTitle',
      link: 'https://www.nature.com/articles/s41467-025-58551-6',
    },
    sourceLinkBot: {
      text: 'llmCitationPotentialSourceBotTitle',
      link: 'https://aclanthology.org/2025.acl-long.348/',
    },
  },
  entity_disambiguation: {
    description: 'entityDisambiguationDescription',
    details: [
      'entityDisambiguationDetail1',
      'entityDisambiguationDetail2',
      'entityDisambiguationDetail3',
      'entityDisambiguationDetail4',
    ],
    sources: [
      'entityDisambiguationSourceTopBullet1',
      'entityDisambiguationSourceTopBullet2',
      'entityDisambiguationSourceTopBullet3',
      'entityDisambiguationSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'entityDisambiguationSourceTopTitle',
      link: 'https://arxiv.org/abs/2407.17125',
    },
  },
  contextual_association: {
    description: 'contextualAssociationDescription',
    details: [
      'contextualAssociationDetail1',
      'contextualAssociationDetail2',
      'contextualAssociationDetail3',
      'contextualAssociationDetail4',
    ],
    sources: [
      'contextualAssociationSourceTopBullet1',
      'contextualAssociationSourceTopBullet2',
      'contextualAssociationSourceTopBullet3',
      'contextualAssociationSourceTopBullet4',
    ],
    sourcesMore: [
      'contextualAssociationSourceBotBullet1',
      'contextualAssociationSourceBotBullet2',
      'contextualAssociationSourceBotBullet3',
      'contextualAssociationSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'contextualAssociationSourceTopTitle',
      link: 'https://arxiv.org/abs/2510.18888',
    },
    sourceLinkBot: {
      text: 'contextualAssociationSourceBotTitle',
      link: 'https://arxiv.org/abs/2506.17580',
    },
  },
  basic_consistency: {
    description: 'basicConsistencyDescription',
    details: [
      'basicConsistencyDetail1',
      'basicConsistencyDetail2',
      'basicConsistencyDetail3',
      'basicConsistencyDetail4',
    ],
    sources: [
      'basicConsistencySourceTopBullet1',
      'basicConsistencySourceTopBullet2',
      'basicConsistencySourceTopBullet3',
      'basicConsistencySourceTopBullet4',
    ],
    sourcesMore: [
      'basicConsistencySourceBotBullet1',
      'basicConsistencySourceBotBullet2',
      'basicConsistencySourceBotBullet3',
      'basicConsistencySourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'basicConsistencySourceTopTitle',
      link: 'https://aclanthology.org/2023.findings-emnlp.728.pdf',
    },
    sourceLinkBot: {
      text: 'basicConsistencySourceBotTitle',
      link: 'https://arxiv.org/abs/2506.02701',
    },
  },
  address_completeness_accuracy: {
    description: 'addressCompletenessAccuracyDescription',
    details: [
      'addressCompletenessAccuracyDetail1',
      'addressCompletenessAccuracyDetail2',
      'addressCompletenessAccuracyDetail3',
      'addressCompletenessAccuracyDetail4',
    ],
    sources: [
      'addressCompletenessAccuracySourceTopBullet1',
      'addressCompletenessAccuracySourceTopBullet2',
      'addressCompletenessAccuracySourceTopBullet3',
      'addressCompletenessAccuracySourceTopBullet4',
    ],
    sourcesMore: [
      'addressCompletenessAccuracySourceBotBullet1',
      'addressCompletenessAccuracySourceBotBullet2',
      'addressCompletenessAccuracySourceBotBullet3',
      'addressCompletenessAccuracySourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'addressCompletenessAccuracySourceTopTitle',
      link: 'https://aclanthology.org/2025.coling-industry.19',
    },
    sourceLinkBot: {
      text: 'addressCompletenessAccuracySourceBotTitle',
      link: 'https://www.tandfonline.com/doi/full/10.1080/17538947.2025.2480268',
    },
  },
  contact_integration: {
    description: 'contactIntegrationDescription',
    details: [
      'contactIntegrationDetail1',
      'contactIntegrationDetail2',
      'contactIntegrationDetail3',
      'contactIntegrationDetail4',
    ],
    sources: [
      'contactIntegrationSourceTopBullet1',
      'contactIntegrationSourceTopBullet2',
      'contactIntegrationSourceTopBullet3',
      'contactIntegrationSourceTopBullet4',
    ],
    sourcesMore: [
      'contactIntegrationSourceBotBullet1',
      'contactIntegrationSourceBotBullet2',
      'contactIntegrationSourceBotBullet3',
      'contactIntegrationSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'contactIntegrationSourceTopTitle',
      link: 'https://www.researchgate.net/publication/383120131_Evaluating_Large_Language_Model_based_Personal_Information_Extraction_and_Countermeasures',
    },
    sourceLinkBot: {
      text: 'contactIntegrationSourceBotTitle',
      link: 'https://www.researchgate.net/publication/335231945_Named_Entity_Recognition_in_Local_Intent_Web_Search_Queries',
    },
  },
  consistency_presentation: {
    description: 'consistencyPresentationDescription',
    details: [
      'consistencyPresentationDetail1',
      'consistencyPresentationDetail2',
      'consistencyPresentationDetail3',
      'consistencyPresentationDetail4',
    ],
    sources: [
      'consistencyPresentationSourceTopBullet1',
      'consistencyPresentationSourceTopBullet2',
      'consistencyPresentationSourceTopBullet3',
      'consistencyPresentationSourceTopBullet4',
    ],
    sourcesMore: [
      'consistencyPresentationSourceBotBullet1',
      'consistencyPresentationSourceBotBullet2',
      'consistencyPresentationSourceBotBullet3',
      'consistencyPresentationSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'consistencyPresentationSourceTopTitle',
      link: 'https://aclanthology.org/2024.lrec-main.1333/',
    },
    sourceLinkBot: {
      text: 'consistencyPresentationSourceBotTitle',
      link: 'https://aclanthology.org/2024.findings-acl.362/',
    },
  },
  service_delivery_evidence: {
    description: 'serviceDeliveryEvidenceDescription',
    details: [
      'serviceDeliveryEvidenceDetail1',
      'serviceDeliveryEvidenceDetail2',
      'serviceDeliveryEvidenceDetail3',
      'serviceDeliveryEvidenceDetail4',
    ],
    sources: [
      'serviceDeliveryEvidenceSourceTopBullet1',
      'serviceDeliveryEvidenceSourceTopBullet2',
      'serviceDeliveryEvidenceSourceTopBullet3',
      'serviceDeliveryEvidenceSourceTopBullet4',
    ],
    sourcesMore: [
      'serviceDeliveryEvidenceSourceBotBullet1',
      'serviceDeliveryEvidenceSourceBotBullet2',
      'serviceDeliveryEvidenceSourceBotBullet3',
      'serviceDeliveryEvidenceSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'serviceDeliveryEvidenceSourceTopTitle',
      link: 'https://openreview.net/forum?id=TqL2xBwXP3',
    },
    sourceLinkBot: {
      text: 'serviceDeliveryEvidenceSourceBotTitle',
      link: 'https://www.acsu.buffalo.edu/~yhu42/papers/2023_ACM_CSUR_LocRef.pdf',
    },
  },
  specificity_for_ai_citation: {
    description: 'specificityForAICitationDescription',
    details: [
      'specificityForAICitationDetail1',
      'specificityForAICitationDetail2',
      'specificityForAICitationDetail3',
      'specificityForAICitationDetail4',
    ],
    sources: [
      'specificityForAICitationSourceTopBullet1',
      'specificityForAICitationSourceTopBullet2',
      'specificityForAICitationSourceTopBullet3',
      'specificityForAICitationSourceTopBullet4',
    ],
    sourcesMore: [
      'specificityForAICitationSourceBotBullet1',
      'specificityForAICitationSourceBotBullet2',
      'specificityForAICitationSourceBotBullet3',
      'specificityForAICitationSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'specificityForAICitationSourceTopTitle',
      link: 'https://acl-bg.org/proceedings/2025/RANLP%202025/pdf/2025.ranlp-1.11.pdf',
    },
    sourceLinkBot: {
      text: 'specificityForAICitationSourceBotTitle',
      link: 'https://www.mdpi.com/2220-9964/11/1/68s',
    },
  },
  evidence_consistency: {
    description: 'evidenceConsistencyDescription',
    details: [
      'evidenceConsistencyDetail1',
      'evidenceConsistencyDetail2',
      'evidenceConsistencyDetail3',
      'evidenceConsistencyDetail4',
    ],
    sources: [
      'evidenceConsistencySourceTopBullet1',
      'evidenceConsistencySourceTopBullet2',
      'evidenceConsistencySourceTopBullet3',
      'evidenceConsistencySourceTopBullet4',
    ],
    sourcesMore: [
      'evidenceConsistencySourceBotBullet1',
      'evidenceConsistencySourceBotBullet2',
      'evidenceConsistencySourceBotBullet3',
      'evidenceConsistencySourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'evidenceConsistencySourceTopTitle',
      link: 'https://arxiv.org/abs/2507.19586',
    },
    sourceLinkBot: {
      text: 'evidenceConsistencySourceBotTitle',
      link: 'https://aclanthology.org/2024.findings-emnlp.596.pdf',
    },
  },
  company_name_schema_readiness: {
    description: 'companyNameSchemaReadinessDescription',
    details: [
      'companyNameSchemaReadinessDetail1',
      'companyNameSchemaReadinessDetail2',
      'companyNameSchemaReadinessDetail3',
      'companyNameSchemaReadinessDetail4',
    ],
    sources: [
      'companyNameSchemaReadinessSourceTopBullet1',
      'companyNameSchemaReadinessSourceTopBullet2',
      'companyNameSchemaReadinessSourceTopBullet3',
      'companyNameSchemaReadinessSourceTopBullet4',
    ],
    sourcesMore: [
      'companyNameSchemaReadinessSourceBotBullet1',
      'companyNameSchemaReadinessSourceBotBullet2',
      'companyNameSchemaReadinessSourceBotBullet3',
      'companyNameSchemaReadinessSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'companyNameSchemaReadinessSourceTopTitle',
      link: 'https://www.nature.com/articles/s41467-024-45563-x',
    },
    sourceLinkBot: {
      text: 'companyNameSchemaReadinessSourceBotTitle',
      link: 'https://arxiv.org/abs/2505.23628',
    },
  },
  business_address_schema_readiness: {
    description: 'businessAddressSchemaReadinessDescription',
    details: [
      'businessAddressSchemaReadinessDetail1',
      'businessAddressSchemaReadinessDetail2',
      'businessAddressSchemaReadinessDetail3',
      'businessAddressSchemaReadinessDetail4',
    ],
    sources: [
      'businessAddressSchemaReadinessSourceTopBullet1',
      'businessAddressSchemaReadinessSourceTopBullet2',
      'businessAddressSchemaReadinessSourceTopBullet3',
      'businessAddressSchemaReadinessSourceTopBullet4',
    ],
    sourcesMore: [
      'businessAddressSchemaReadinessSourceBotBullet1',
      'businessAddressSchemaReadinessSourceBotBullet2',
      'businessAddressSchemaReadinessSourceBotBullet3',
      'businessAddressSchemaReadinessSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'businessAddressSchemaReadinessSourceTopTitle',
      link: 'https://www.semantic-web-journal.net/content/llm4schemaorg-generating-schemaorg-markups-large-language-models',
    },
    sourceLinkBot: {
      text: 'businessAddressSchemaReadinessSourceBotTitle',
      link: 'https://www.microsoft.com/en-us/research/publication/table-meets-llm-can-large-language-models-understand-structured-table-data-a-benchmark-and-empirical-study/',
    },
  },
  geographic_focus_schema_readiness: {
    description: 'geographicFocusSchemaReadinessDescription',
    details: [
      'geographicFocusSchemaReadinessDetail1',
      'geographicFocusSchemaReadinessDetail2',
      'geographicFocusSchemaReadinessDetail3',
      'geographicFocusSchemaReadinessDetail4',
    ],
    sources: [
      'geographicFocusSchemaReadinessSourceTopBullet1',
      'geographicFocusSchemaReadinessSourceTopBullet2',
      'geographicFocusSchemaReadinessSourceTopBullet3',
      'geographicFocusSchemaReadinessSourceTopBullet4',
    ],
    sourcesMore: [
      'geographicFocusSchemaReadinessSourceBotBullet1',
      'geographicFocusSchemaReadinessSourceBotBullet2',
      'geographicFocusSchemaReadinessSourceBotBullet3',
      'geographicFocusSchemaReadinessSourceBotBullet4',
    ],
    sourceLinkTop: {
      text: 'geographicFocusSchemaReadinessSourceTopTitle',
      link: 'https://www.vldb.org/2025/Workshops/VLDB-Workshops-2025/LLM+Graph/LLMGraph-5.pdf',
    },
    sourceLinkBot: {
      text: 'geographicFocusSchemaReadinessSourceBotTitle',
      link: 'https://aclanthology.org/2024.findings-acl.362/',
    },
  },
  conversational_content_writing: {
    description: 'conversationalContentWritingDescription',
    details: [
      'conversationalContentWritingDetail1',
      'conversationalContentWritingDetail2',
      'conversationalContentWritingDetail3',
      'conversationalContentWritingDetail4',
    ],
    sources: [
      'conversationalContentWritingSourceTopBullet1',
      'conversationalContentWritingSourceTopBullet2',
      'conversationalContentWritingSourceTopBullet3',
    ],
    sourcesMore: [
      'conversationalContentWritingSourceBotBullet1',
      'conversationalContentWritingSourceBotBullet2',
      'conversationalContentWritingSourceBotBullet3',
    ],
    sourceLinkTop: {
      text: 'conversationalContentWritingSourceTopTitle',
      link: 'https://arxiv.org/abs/2311.09735',
    },
    sourceLinkBot: {
      text: 'conversationalContentWritingSourceBotTitle',
      link: 'https://arxiv.org/abs/2409.15890',
    },
  },
  statistical_evidence_integration: {
    description: 'statisticalEvidenceIntegrationDescription',
    details: [
      'statisticalEvidenceIntegrationDetail1',
      'statisticalEvidenceIntegrationDetail2',
      'statisticalEvidenceIntegrationDetail3',
    ],
    sources: [
      'statisticalEvidenceIntegrationSourceTopBullet1',
      'statisticalEvidenceIntegrationSourceTopBullet2',
      'statisticalEvidenceIntegrationSourceTopBullet3',
    ],
    sourcesMore: [
      'statisticalEvidenceIntegrationSourceBotBullet1',
      'statisticalEvidenceIntegrationSourceBotBullet2',
      'statisticalEvidenceIntegrationSourceBotBullet3',
    ],
    sourceLinkTop: {
      text: 'statisticalEvidenceIntegrationSourceTopTitle',
      link: 'https://arxiv.org/abs/2305.14251',
    },
    sourceLinkBot: {
      text: 'statisticalEvidenceIntegrationSourceBotTitle',
      link: 'https://neurips.cc/virtual/2023/poster/73517',
    },
  },
  content_freshness_indicators: {
    description: 'contentFreshnessIndicatorsDescription',
    details: [
      'contentFreshnessIndicatorsDetail1',
      'contentFreshnessIndicatorsDetail2',
      'contentFreshnessIndicatorsDetail3',
      'contentFreshnessIndicatorsDetail4',
    ],
    sources: [
      'contentFreshnessIndicatorsSourceTopBullet1',
      'contentFreshnessIndicatorsSourceTopBullet2',
      'contentFreshnessIndicatorsSourceTopBullet3',
      'contentFreshnessIndicatorsSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'contentFreshnessIndicatorsSourceTopTitle',
      link: 'https://arxiv.org/abs/2509.11353',
    },
  },
  llm_implementation: {
    description: 'llmImplementationDescription',
    details: [
      'llmImplementationDetail1',
      'llmImplementationDetail2',
      'llmImplementationDetail3',
      'llmImplementationDetail4',
    ],
    sources: [
      'llmImplementationSourceTopBullet1',
      'llmImplementationSourceTopBullet2',
      'llmImplementationSourceTopBullet3',
      'llmImplementationSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'llmImplementationSourceTopTitle',
      link: 'http://answer.ai/',
    },
    sourcesMore: [
      'llmImplementationSourceBotBullet1',
      'llmImplementationSourceBotBullet2',
      'llmImplementationSourceBotBullet3',
    ],
    sourceLinkBot: {
      text: 'llmImplementationSourceBotTitle',
      link: '',
    },
  },
  'robots-txt': {
    description: 'robotsTxtCrawlerPermissionsDescription',
    details: [
      'robotsTxtCrawlerPermissionsDetail1',
      'robotsTxtCrawlerPermissionsDetail2',
      'robotsTxtCrawlerPermissionsDetail3',
      'robotsTxtCrawlerPermissionsDetail4',
    ],
    sources: [
      'robotsTxtCrawlerPermissionsSourceTopBullet1',
      'robotsTxtCrawlerPermissionsSourceTopBullet2',
      'robotsTxtCrawlerPermissionsSourceTopBullet3',
      'robotsTxtCrawlerPermissionsSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'robotsTxtCrawlerPermissionsSourceTopTitle',
      link: 'https://developers.google.com/search/docs/crawling-indexing/robots/intro',
    },
  },
  file: {
    description: 'llmsTxtFileExistsDescription',
    details: [
      'llmsTxtFileExistsDetail1',
      'llmsTxtFileExistsDetail2',
      'llmsTxtFileExistsDetail3',
      'llmsTxtFileExistsDetail4',
    ],
    sources: [
      'llmsTxtFileExistsSourceTopBullet1',
      'llmsTxtFileExistsSourceTopBullet2',
      'llmsTxtFileExistsSourceTopBullet3',
      'llmsTxtFileExistsSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'llmsTxtFileExistsSourceTopTitle',
      link: 'https://llmstxt.org/',
    },
  },
  structure: {
    description: 'llmsTxtStructureDescription',
    details: [
      'llmsTxtStructureDetail1',
      'llmsTxtStructureDetail2',
      'llmsTxtStructureDetail3',
      'llmsTxtStructureDetail4',
    ],
    sources: [
      'llmsTxtStructureSourceTopBullet1',
      'llmsTxtStructureSourceTopBullet2',
      'llmsTxtStructureSourceTopBullet3',
      'llmsTxtStructureSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'llmsTxtStructureSourceTopTitle',
      link: 'https://llmstxt.org/',
    },
  },
  products_section: {
    description: 'productsOrServicesSectionDescription',
    details: [
      'productsOrServicesSectionDetail1',
      'productsOrServicesSectionDetail2',
      'productsOrServicesSectionDetail3',
      'productsOrServicesSectionDetail4',
    ],
    sources: [
      'productsOrServicesSectionSourceTopBullet1',
      'productsOrServicesSectionSourceTopBullet2',
      'productsOrServicesSectionSourceTopBullet3',
      'productsOrServicesSectionSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'productsOrServicesSectionSourceTopTitle',
      link: 'https://llmstxt.org/',
    },
  },
  schema_exists: {
    description: 'schemaOrgExistsDescription',
    details: [
      'schemaOrgExistsDetail1',
      'schemaOrgExistsDetail2',
      'schemaOrgExistsDetail3',
      'schemaOrgExistsDetail4',
    ],
    sources: [
      'schemaOrgExistsSourceTopBullet1',
      'schemaOrgExistsSourceTopBullet2',
      'schemaOrgExistsSourceTopBullet3',
      'schemaOrgExistsSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'schemaOrgExistsSourceTopTitle',
      link: 'https://schema.org/',
    },
  },
  organization_schema: {
    description: 'organizationSchemaPresentDescription',
    details: [
      'organizationSchemaPresentDetail1',
      'organizationSchemaPresentDetail2',
      'organizationSchemaPresentDetail3',
      'organizationSchemaPresentDetail4',
    ],
    sources: [
      'organizationSchemaPresentSourceTopBullet1',
      'organizationSchemaPresentSourceTopBullet2',
      'organizationSchemaPresentSourceTopBullet3',
      'organizationSchemaPresentSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'organizationSchemaPresentSourceTopTitle',
      link: 'https://schema.org/Organization',
    },
  },
  website_schema: {
    description: 'websiteSchemaPresentDescription',
    details: [
      'websiteSchemaPresentDetail1',
      'websiteSchemaPresentDetail2',
      'websiteSchemaPresentDetail3',
      'websiteSchemaPresentDetail4',
    ],
    sources: [
      'websiteSchemaPresentSourceTopBullet1',
      'websiteSchemaPresentSourceTopBullet2',
      'websiteSchemaPresentSourceTopBullet3',
      'websiteSchemaPresentSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'websiteSchemaPresentSourceTopTitle',
      link: 'https://schema.org/WebSite',
    },
  },
  js_crawlability: {
    description: 'jsCrawlabilityDescription',
    details: [
      'jsCrawlabilityDetail1',
      'jsCrawlabilityDetail2',
      'jsCrawlabilityDetail3',
      'jsCrawlabilityDetail4',
    ],
    sources: [
      'jsCrawlabilitySourceTopBullet1',
      'jsCrawlabilitySourceTopBullet2',
      'jsCrawlabilitySourceTopBullet3',
    ],
    sourceLinkTop: {
      text: 'jsCrawlabilitySourceTopTitle',
      link: '',
    },
    sourcesMore: [
      'jsCrawlabilitySourceBotBullet1',
      'jsCrawlabilitySourceBotBullet2',
      'jsCrawlabilitySourceBotBullet3',
    ],
    sourceLinkBot: {
      text: 'jsCrawlabilitySourceBotTitle',
      link: '',
    },
  },
  speed_of_load: {
    description: 'speedOfLoadDescription',
    details: [
      'speedOfLoadDetail1',
      'speedOfLoadDetail2',
      'speedOfLoadDetail3',
      'speedOfLoadDetail4',
    ],
    sources: [
      'speedOfLoadSourceTopBullet1',
      'speedOfLoadSourceTopBullet2',
      'speedOfLoadSourceTopBullet3',
    ],
    sourceLinkTop: {
      text: 'speedOfLoadSourceTopTitle',
      link: '',
    },
    sourcesMore: [
      'speedOfLoadSourceBotBullet1',
      'speedOfLoadSourceBotBullet2',
      'speedOfLoadSourceBotBullet3',
      'speedOfLoadSourceBotBullet4',
    ],
    sourceLinkBot: {
      text: 'speedOfLoadSourceBotTitle',
      link: '',
    },
  },
  core_web_vitals: {
    description: 'coreWebVitalsDescription',
    details: [
      'coreWebVitalsDetail1',
      'coreWebVitalsDetail2',
      'coreWebVitalsDetail3',
      'coreWebVitalsDetail4',
      'coreWebVitalsDetail5',
    ],
    sources: [
      'coreWebVitalsSourceTopBullet1',
      'coreWebVitalsSourceTopBullet2',
      'coreWebVitalsSourceTopBullet3',
      'coreWebVitalsSourceTopBullet4',
    ],
    sourceLinkTop: {
      text: 'coreWebVitalsSourceTopTitle',
      link: '',
    },
    sourcesMore: [
      'coreWebVitalsSourceBotBullet1',
      'coreWebVitalsSourceBotBullet2',
      'coreWebVitalsSourceBotBullet3',
      'coreWebVitalsSourceBotBullet4',
    ],
    sourceLinkBot: {
      text: 'coreWebVitalsSourceBotTitle',
      link: '',
    },
  },
};
